import { useState, useRef, useCallback } from 'react';
import { VoiceViewTab } from '@/types/voice/favorites';
import { VoiceFilterState } from '@/types/voice/voice-playground';

export interface TabState {
  page: number;
  searchInput: string;
  search: string;
  filters: VoiceFilterState;
}

const createEmptyTabState = (): Omit<TabState, 'searchTimeoutRef'> => ({
  page: 0,
  searchInput: '',
  search: '',
  filters: {
    category: 'all',
    accent: 'all',
    language: 'all',
    age: 'all',
    gender: 'all',
  },
});

export const useTabFiltering = (initialTab: VoiceViewTab = 'recommended') => {
  const [activeTab, setActiveTab] = useState<VoiceViewTab>(initialTab);

  // Initialize tab states for all tabs
  const [tabStates, setTabStates] = useState<Record<VoiceViewTab, Omit<TabState, 'searchTimeoutRef'>>>({
    recommended: createEmptyTabState(),
    all: createEmptyTabState(),
    favorites: createEmptyTabState(),
  });

  // Refs for debouncing search per tab
  const searchTimeoutRefs = useRef<Record<VoiceViewTab, NodeJS.Timeout | undefined>>({
    recommended: undefined,
    all: undefined,
    favorites: undefined,
  });

  const currentTab = tabStates[activeTab];
  const currentSearchTimeoutRef = searchTimeoutRefs.current[activeTab];

  const updateSearch = useCallback((searchInput: string) => {
    setTabStates(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        searchInput,
        page: 0,
      },
    }));

    // Clear existing timeout for this tab
    if (searchTimeoutRefs.current[activeTab]) {
      clearTimeout(searchTimeoutRefs.current[activeTab]);
    }

    // Set new timeout for debounced search
    searchTimeoutRefs.current[activeTab] = setTimeout(() => {
      setTabStates(prev => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          search: searchInput,
        },
      }));
    }, 500);
  }, [activeTab]);

  const updateFilter = useCallback((filterKey: keyof VoiceFilterState, value: string) => {
    setTabStates(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        filters: {
          ...prev[activeTab].filters,
          [filterKey]: value,
        },
        page: 0,
      },
    }));
  }, [activeTab]);

  const switchTab = useCallback((tab: VoiceViewTab) => {
    // Clear any pending search timeout on old tab
    if (searchTimeoutRefs.current[activeTab]) {
      clearTimeout(searchTimeoutRefs.current[activeTab]);
    }

    // Reset new tab to initial state
    setTabStates(prev => ({
      ...prev,
      [tab]: createEmptyTabState(),
    }));

    setActiveTab(tab);
  }, [activeTab]);

  const resetFilters = useCallback(() => {
    setTabStates(prev => ({
      ...prev,
      [activeTab]: createEmptyTabState(),
    }));

    // Clear any pending search timeout
    if (searchTimeoutRefs.current[activeTab]) {
      clearTimeout(searchTimeoutRefs.current[activeTab]);
    }
  }, [activeTab]);

  const loadMore = useCallback(() => {
    setTabStates(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        page: prev[activeTab].page + 1,
      },
    }));
  }, [activeTab]);

  return {
    activeTab,
    currentTab,
    currentSearchTimeoutRef,
    switchTab,
    updateSearch,
    updateFilter,
    resetFilters,
    loadMore,
  };
};
